// useWishlist — localStorage-backed wishlist for the storefront, with an
// automatic server-side sync once the visitor signs in with Google.
//
// Design:
//   • Anonymous shoppers: localStorage is the single source of truth. Toggles
//     also POST an event to /api/wishlist/event for demand analytics.
//   • Signed-in customers: on first render after sign-in we (a) PUT the
//     merged local+server list to /api/wishlist/me so nothing is lost, then
//     (b) GET the fresh server list and write it back into localStorage.
//     Subsequent mutations PUT the full list to the server (fire-and-forget).
//
// Emits a wishlist:changed CustomEvent on every mutation so the nav badge
// counter and any other listeners can re-render without prop drilling.

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useUser from '@/hooks/useUser';

const STORAGE_KEY = 'ecobike.wishlist.v1';
const SESSION_KEY = 'ecobike.wishlist.session_id.v1';
const CHANGE_EVENT = 'wishlist:changed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeList(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function readOrCreateSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    // Simple UUID-ish generator — enough entropy for anonymous analytics.
    sid = (crypto?.randomUUID?.() || 'sid_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

async function logEvent(event, slug, meta = {}) {
  try {
    await axios.post(`${API}/wishlist/event`, {
      session_id: readOrCreateSessionId(),
      slug,
      event,
      product_name: meta.name || null,
      product_price: meta.price || null,
    }, { timeout: 6000 });
  } catch {
    // Best-effort — silent fail so a network hiccup never breaks the UI.
  }
}

/** Push the current local list up to the server (signed-in only). */
async function pushToServer(items) {
  try {
    await axios.put(
      `${API}/wishlist/me`,
      { items },
      { withCredentials: true, timeout: 6000 },
    );
  } catch {
    // Fire-and-forget — server will catch up on next sign-in.
  }
}

/** Merge two wishlist arrays by slug (last-writer keeps richer fields). */
function mergeBySlug(a, b) {
  const bySlug = new Map();
  for (const it of a) bySlug.set(it.slug, { ...it });
  for (const it of b) {
    const prev = bySlug.get(it.slug) || {};
    bySlug.set(it.slug, { ...prev, ...it });
  }
  return Array.from(bySlug.values());
}

export default function useWishlist() {
  const [items, setItems] = useState(readList);
  const { user } = useUser();
  const syncedForUserId = useRef(null);

  useEffect(() => {
    const onChange = () => setItems(readList());
    window.addEventListener(CHANGE_EVENT, onChange);
    // Also update when another tab modifies the same localStorage key.
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  // Sign-in sync: once per session per user, merge local ⇄ server.
  useEffect(() => {
    if (!user?.user_id) {
      syncedForUserId.current = null;
      return;
    }
    if (syncedForUserId.current === user.user_id) return;
    syncedForUserId.current = user.user_id;

    (async () => {
      try {
        const local = readList();
        const res = await axios.get(`${API}/wishlist/me`, {
          withCredentials: true,
          timeout: 6000,
        });
        const server = Array.isArray(res.data?.items) ? res.data.items : [];
        const merged = mergeBySlug(local, server);
        // Push merged back so the server carries any local-only additions.
        await pushToServer(merged);
        writeList(merged);
      } catch {
        // Network hiccup: keep local as-is; will retry next mount.
      }
    })();
  }, [user]);

  const isSaved = useCallback((slug) => items.some((it) => it.slug === slug), [items]);

  const toggle = useCallback((product) => {
    const current = readList();
    const already = current.some((it) => it.slug === product.slug);
    let next;
    if (already) {
      next = current.filter((it) => it.slug !== product.slug);
      logEvent('remove', product.slug, product);
    } else {
      next = [...current, { slug: product.slug, name: product.name, price: product.price, image: product.image, addedAt: Date.now() }];
      logEvent('add', product.slug, product);
    }
    writeList(next);
    if (user?.user_id) pushToServer(next);
    return !already;
  }, [user]);

  const remove = useCallback((slug) => {
    const current = readList();
    const target = current.find((it) => it.slug === slug);
    if (!target) return;
    const next = current.filter((it) => it.slug !== slug);
    writeList(next);
    logEvent('remove', slug, target);
    if (user?.user_id) pushToServer(next);
  }, [user]);

  const clear = useCallback(() => {
    const current = readList();
    current.forEach((it) => logEvent('remove', it.slug, it));
    writeList([]);
    if (user?.user_id) pushToServer([]);
  }, [user]);

  return { items, count: items.length, isSaved, toggle, remove, clear };
}
